"use client"
import React, { useState } from "react"
import {
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Button,
  Grid,
} from "@mui/material"
import LocationAutocomplete from "./LocationAutocomplete"
import ProviderAutocomplete from "./ProviderAutocomplete"
import SpecialtyAutocomplete from "./SpecialtyAutocomplete"

const FilterSidebar = ({ searchCriteria, filters, onFilterChange, onUpdate, loading, isPublicRoute = false }) => {
  const [errors, setErrors] = useState({})

  const handleFilterUpdate = (field, value) => {
    const newFilters = { ...filters, [field]: value }
    onFilterChange(newFilters)

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    if ((field === "provider" || field === "specialty") && value) {
      setErrors((prev) => ({ ...prev, provider: null, specialty: null }))
    }

    if (field === "location" && value && value.placeName) {
      setErrors((prev) => ({ ...prev, location: null }))
    }
  }

  const handleLocationError = (error) => {
    console.error("Location filter error:", error)
  }

  const validateFilters = () => {
    const newErrors = {}

    if (!filters.location || !filters.location.placeName) {
      newErrors.location = "Location is required"
    }

    const providerText = typeof filters.provider === "string" ? filters.provider.trim() : filters.provider?.name
    if (!providerText && !filters.specialty) {
      newErrors.provider = "Either Provider name or Specialty name is required"
      newErrors.specialty = "Either Provider name or Specialty name is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleUpdateClick = () => {
    if (validateFilters()) {
      onUpdate()
    }
  }

  const distanceOptions = [
    { value: 5, label: "5 miles" },
    { value: 10, label: "10 miles" },
    { value: 15, label: "15 miles" },
    { value: 20, label: "20 miles" },
    { value: 25, label: "25 miles" },
    { value: 30, label: "30 miles" },
  ]

  return (
    <Paper 
      sx={{ 
        p: 1, 
        borderRadius: 2, 
        height: "fit-content",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        border: "1px solid #e5e7eb"
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Provider */}
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel 
              shrink 
              sx={{ 
                fontSize: "0.875rem", 
                color: "#6b7280",
                position: 'relative',
                opacity: 'unset',
                fontWeight: 500,
                "&.Mui-focused": {
                  color: "#035fcb"
                }
              }}
            >
              Provider
            </InputLabel>
            <ProviderAutocomplete
              value={filters.provider}
              onChange={(value) => handleFilterUpdate("provider", value)}
              size="small"
              error={!!errors.provider}
            />
            {errors.provider && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {errors.provider}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Specialty */}
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel 
              shrink 
              sx={{ 
                fontSize: "0.875rem", 
                color: "#6b7280",
                position: 'relative',
                opacity: 'unset',
                fontWeight: 500,
                "&.Mui-focused": {
                  color: "#035fcb"
                }
              }}
            >
              Specialty
            </InputLabel>
            <SpecialtyAutocomplete
              value={filters.specialty}
              onChange={(value) => handleFilterUpdate("specialty", value)}
              size="small"
              isPublicRoute={isPublicRoute}
              error={!!errors.specialty}
            />
            {errors.specialty && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {errors.specialty}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Distance */}
        <Grid item xs={12} sm={6} md={1.4}>
          <FormControl fullWidth size="small">
            <InputLabel 
              sx={{ 
                fontSize: "0.875rem", 
                color: "#6b7280",
                position: 'relative',
                opacity: 'unset',
                fontWeight: 500,
                "&.Mui-focused": {
                  color: "#035fcb"
                }
              }}
            >
              Distance
            </InputLabel>
            <Select
              value={filters.distance}
              label="Distance"
              onChange={(e) => handleFilterUpdate("distance", e.target.value)}
              sx={{ 
                borderRadius: 1.5,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d1d5db"
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#9ca3af"
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#035fcb"
                }
              }}
            >
              {distanceOptions.map((option) => (
                <MenuItem sx={{ml: 1, pl: 1}} key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Location */}
        <Grid item xs={12} sm={6} md={3.8}>
          <FormControl fullWidth size="small">
            <InputLabel 
              shrink 
              sx={{ 
                fontSize: "0.875rem", 
                color: "#6b7280",
                position: 'relative',
                opacity: 'unset',
                fontWeight: 500,
                "&.Mui-focused": {
                  color: "#035fcb"
                }
              }}
            >
              Location <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <LocationAutocomplete
              value={filters.location}
              onChange={(value) => handleFilterUpdate("location", value)}
              onError={handleLocationError}
              placeholder="Enter location"
              showUseCurrentButton={false}
              autoDetect={false}
              size="small"
              error={!!errors.location}
            />
            {errors.location && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {errors.location}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Update Button */}
        <Grid item xs={12} sm={6} md={2}>
          <InputLabel
            shrink
            sx={{
              fontSize: "0.875rem",
              position: 'relative',
              opacity: 'unset',
              fontWeight: 500,
              visibility: "hidden",
            }}
          >
            Update
          </InputLabel>
          <Button
            variant="contained"
            fullWidth
            disabled={loading}
            onClick={handleUpdateClick}
            size="medium"
            sx={{
              py: 1.25,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              backgroundColor: "#035fcb",
              "&:hover": {
                backgroundColor: "#035fcb"
              },
              "&:disabled": {
                backgroundColor: "#9ca3af"
              }
            }}
          >
            {loading ? "Updating..." : "Update results"}
          </Button>
        </Grid>
      </Grid>

    </Paper>
  )
}

export default FilterSidebar